import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AssignManagerLearningCourseDocument,
  AssignManagerLearningCourseMutation,
  AssignManagerLearningCourseMutationVariables,
  AssignManagerLearningPathDocument,
  AssignManagerLearningPathMutation,
  AssignManagerLearningPathMutationVariables,
  ManagerLearningAssignmentsDocument,
  ManagerLearningAssignmentsQuery,
  ManagerLearningAssignmentsQueryVariables,
  ManagerLearningCatalogAndCaregiversDocument,
  ManagerLearningCatalogAndCaregiversQuery,
  ManagerLearningCatalogAndCaregiversQueryVariables,
  UnassignManagerLearningCourseDocument,
  UnassignManagerLearningCourseMutation,
  UnassignManagerLearningCourseMutationVariables,
  UnassignManagerLearningPathDocument,
  UnassignManagerLearningPathMutation,
  UnassignManagerLearningPathMutationVariables,
} from "../generated/graphql";
import { client } from "../lib/apolloClient";

export type ManagerLearningCaregiver = ManagerLearningCatalogAndCaregiversQuery["managerLearningCaregivers"][number];

export type ManagerLearningCourse =
  ManagerLearningCatalogAndCaregiversQuery["managerLearningCatalog"]["courses"][number];

export type ManagerLearningCourseAccess =
  ManagerLearningCatalogAndCaregiversQuery["managerLearningCatalog"]["courseAccess"][number];

type LearningPathCourse = {
  courseId: string;
  position: number;
};

export type ManagerLearningPath = {
  id: string;
  version: string;
  locale: string;
  title: string;
  description: string;
  thumbnailFilename: string;
  enforceOrder: boolean;
  courses: LearningPathCourse[];
  estimatedDurationMinutes: number;
  tags: string[];
};

export type ManagerLearningAssignment = ManagerLearningAssignmentsQuery["managerLearningAssignments"][number];

type AssignmentItemKind = "course" | "learningPath";

type LearningAssignmentState = {
  caregivers: ManagerLearningCaregiver[];
  courses: ManagerLearningCourse[];
  courseAccess: ManagerLearningCourseAccess[];
  learningPaths: ManagerLearningPath[];
  assignments: ManagerLearningAssignment[];
  loading: boolean;
  error: Error | null;
};

const emptyData = {
  caregivers: [],
  courses: [],
  courseAccess: [],
  learningPaths: [],
  assignments: [],
};

export class LearningAssignmentConflictError extends Error {
  constructor() {
    super("An assignment update for this caregiver and item is already running");
    this.name = "LearningAssignmentConflictError";
  }
}

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error("Manager learning assignment request failed");

const mapCatalog = (
  data: ManagerLearningCatalogAndCaregiversQuery
): Pick<LearningAssignmentState, "caregivers" | "courses" | "courseAccess" | "learningPaths"> => {
  const courses = [...data.managerLearningCatalog.courses].sort((left, right) =>
    left.title.localeCompare(right.title, "de-DE")
  );
  const coursesById = new Map(courses.map(course => [course.id, course]));
  const learningPaths = data.managerLearningCatalog.learningPaths
    .map(path => {
      const memberCourses = [...path.courses]
        .sort((left, right) => left.position - right.position)
        .map(member => coursesById.get(member.courseId))
        .filter((course): course is ManagerLearningCourse => course != null);

      return {
        ...path,
        estimatedDurationMinutes: memberCourses.reduce((total, course) => total + course.estimatedDurationMinutes, 0),
        tags: Array.from(new Set(memberCourses.flatMap(course => course.tags))),
      };
    })
    .sort((left, right) => left.title.localeCompare(right.title, "de-DE"));
  const caregivers = [...data.managerLearningCaregivers].sort((left, right) =>
    left.displayName.localeCompare(right.displayName, "de-DE")
  );

  return { caregivers, courses, courseAccess: data.managerLearningCatalog.courseAccess ?? [], learningPaths };
};

const assignmentKey = (userId: string, itemKind: AssignmentItemKind, itemId: string): string =>
  `${userId}:${itemKind}:${itemId}`;

const assignmentMatches = (
  assignment: ManagerLearningAssignment,
  userId: string,
  itemKind: AssignmentItemKind,
  itemId: string
): boolean =>
  assignment.userId === userId &&
  assignment.kind === "MANAGER" &&
  (itemKind === "course"
    ? assignment.itemKind === "COURSE" && assignment.courseId === itemId
    : assignment.itemKind === "LEARNING_PATH" && assignment.learningPathId === itemId);

const validateMutationResult = (
  assignment: ManagerLearningAssignment | null | undefined,
  userId: string,
  itemKind: AssignmentItemKind,
  itemId: string,
  assigned: boolean
): ManagerLearningAssignment => {
  if (
    !assignment ||
    assignment.active !== assigned ||
    assignment.userId !== userId ||
    assignment.kind !== "MANAGER" ||
    (itemKind === "course"
      ? assignment.itemKind !== "COURSE" || assignment.courseId !== itemId
      : assignment.itemKind !== "LEARNING_PATH" || assignment.learningPathId !== itemId)
  ) {
    throw new Error("Learning assignment mutation returned conflicting state");
  }
  return assignment;
};

export const useLearningAssignments = () => {
  const [state, setState] = useState<LearningAssignmentState>({
    ...emptyData,
    loading: true,
    error: null,
  });
  const [pendingKeys, setPendingKeys] = useState<string[]>([]);
  const pendingKeysRef = useRef(new Set<string>());
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const load = useCallback(async (preserveData = false): Promise<void> => {
    const requestId = ++requestIdRef.current;
    setState(previous => ({
      ...(preserveData
        ? {
            caregivers: previous.caregivers,
            courses: previous.courses,
            courseAccess: previous.courseAccess,
            learningPaths: previous.learningPaths,
            assignments: previous.assignments,
          }
        : emptyData),
      loading: !preserveData,
      error: null,
    }));

    try {
      const catalogResult = await client.query<
        ManagerLearningCatalogAndCaregiversQuery,
        ManagerLearningCatalogAndCaregiversQueryVariables
      >({
        query: ManagerLearningCatalogAndCaregiversDocument,
        fetchPolicy: "network-only",
      });
      const catalog = mapCatalog(catalogResult.data);
      const userIds = catalog.caregivers.map(caregiver => caregiver.userId);
      const assignments =
        userIds.length === 0
          ? []
          : (
              await client.query<ManagerLearningAssignmentsQuery, ManagerLearningAssignmentsQueryVariables>({
                query: ManagerLearningAssignmentsDocument,
                variables: { userIds },
                fetchPolicy: "network-only",
              })
            ).data.managerLearningAssignments.filter(assignment => assignment.active);

      if (requestId !== requestIdRef.current || !mountedRef.current) return;
      setState({ ...catalog, assignments, loading: false, error: null });
    } catch (error) {
      if (requestId !== requestIdRef.current || !mountedRef.current) return;
      setState(previous => ({
        ...(preserveData
          ? {
              caregivers: previous.caregivers,
              courses: previous.courses,
              courseAccess: previous.courseAccess,
              learningPaths: previous.learningPaths,
              assignments: previous.assignments,
            }
          : emptyData),
        loading: false,
        error: toError(error),
      }));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, [load]);

  const changeAssignment = useCallback(
    async (userId: string, itemKind: AssignmentItemKind, itemId: string, assigned: boolean): Promise<void> => {
      const pendingKey = assignmentKey(userId, itemKind, itemId);
      if (pendingKeysRef.current.has(pendingKey)) throw new LearningAssignmentConflictError();

      pendingKeysRef.current.add(pendingKey);
      if (mountedRef.current) setPendingKeys(Array.from(pendingKeysRef.current));
      try {
        let mutationAssignment: ManagerLearningAssignment | undefined;
        if (itemKind === "course") {
          if (assigned) {
            const result = await client.mutate<
              AssignManagerLearningCourseMutation,
              AssignManagerLearningCourseMutationVariables
            >({
              mutation: AssignManagerLearningCourseDocument,
              variables: { input: { userId, courseId: itemId } },
            });
            mutationAssignment = result.data?.assignLearningCourse.assignment;
          } else {
            const result = await client.mutate<
              UnassignManagerLearningCourseMutation,
              UnassignManagerLearningCourseMutationVariables
            >({
              mutation: UnassignManagerLearningCourseDocument,
              variables: { input: { userId, courseId: itemId } },
            });
            mutationAssignment = result.data?.unassignLearningCourse.assignment;
          }
        } else {
          if (assigned) {
            const result = await client.mutate<
              AssignManagerLearningPathMutation,
              AssignManagerLearningPathMutationVariables
            >({
              mutation: AssignManagerLearningPathDocument,
              variables: { input: { userId, learningPathId: itemId } },
            });
            mutationAssignment = result.data?.assignLearningPath.assignment;
          } else {
            const result = await client.mutate<
              UnassignManagerLearningPathMutation,
              UnassignManagerLearningPathMutationVariables
            >({
              mutation: UnassignManagerLearningPathDocument,
              variables: { input: { userId, learningPathId: itemId } },
            });
            mutationAssignment = result.data?.unassignLearningPath.assignment;
          }
        }
        const returnedAssignment = validateMutationResult(mutationAssignment, userId, itemKind, itemId, assigned);

        if (!mountedRef.current) return;
        setState(previous => ({
          ...previous,
          assignments: assigned
            ? [
                ...previous.assignments.filter(assignment => !assignmentMatches(assignment, userId, itemKind, itemId)),
                returnedAssignment,
              ]
            : previous.assignments.filter(assignment => !assignmentMatches(assignment, userId, itemKind, itemId)),
        }));
      } finally {
        pendingKeysRef.current.delete(pendingKey);
        if (mountedRef.current) setPendingKeys(Array.from(pendingKeysRef.current));
      }
    },
    []
  );

  const setCourseAssignment = useCallback(
    (userId: string, courseId: string, assigned: boolean) => changeAssignment(userId, "course", courseId, assigned),
    [changeAssignment]
  );
  const setLearningPathAssignment = useCallback(
    (userId: string, learningPathId: string, assigned: boolean) =>
      changeAssignment(userId, "learningPath", learningPathId, assigned),
    [changeAssignment]
  );
  const pendingAssignmentKeys = useMemo(() => new Set(pendingKeys), [pendingKeys]);
  const isAssignmentPending = useCallback(
    (userId: string, itemKind: AssignmentItemKind, itemId: string) =>
      pendingAssignmentKeys.has(assignmentKey(userId, itemKind, itemId)),
    [pendingAssignmentKeys]
  );
  const courseAccessById = useMemo(
    () => new Map(state.courseAccess.map(access => [access.courseId as string, access])),
    [state.courseAccess]
  );

  return {
    ...state,
    courseAccessById,
    isCaregiverEmpty: !state.loading && !state.error && state.caregivers.length === 0,
    isCatalogEmpty: !state.loading && !state.error && state.courses.length === 0 && state.learningPaths.length === 0,
    pendingAssignmentKeys,
    isAssignmentPending,
    reload: load,
    setCourseAssignment,
    setLearningPathAssignment,
  };
};
